import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Star, ArrowRight, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const SanBernardinoCA = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Mobile Car Detailing in San Bernardino, CA | Precision Mobile Detailing';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile car detailing in San Bernardino, CA. Serving CSUSB, Shandin Hills, Downtown, and all neighborhoods. Premium auto detailing at your location.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Precision Mobile Detailing - San Bernardino",
      "image": "https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png",
      "@id": "",
      "url": window.location.href,
      "telephone": businessInfo.phone,
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Bernardino",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "City",
        "name": "San Bernardino",
        "addressRegion": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 34.1083,
        "longitude": -117.2898
      },
      "description": "Professional mobile car detailing service in San Bernardino, CA",
      "serviceType": ["Auto Detailing", "Car Detailing", "Mobile Detailing"]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { name: 'Full Detail Package', link: '/services' },
    { name: 'Essential Detail', link: '/services' },
    { name: 'Clay and Seal', link: '/services' },
    { name: 'Interior Reset', link: '/services' }
  ];

  const faqs = [
    {
      question: 'Do you provide mobile detailing near CSUSB campus?',
      answer: 'Yes! We serve California State University San Bernardino campus area, student housing, and all surrounding neighborhoods. Our mobile service is ideal for busy students and faculty who need convenient car care.'
    },
    {
      question: 'Can you detail cars in Shandin Hills and Verdemont?',
      answer: 'Absolutely. We provide mobile car detailing throughout Shandin Hills, Verdemont, Del Rosa, and all San Bernardino residential areas. We come directly to your home or preferred location.'
    },
    {
      question: 'How does mobile car detailing work in San Bernardino?',
      answer: 'We arrive at your San Bernardino location fully equipped with water, power equipment, and professional products. You simply provide a safe parking spot. We handle everything else.'
    },
    {
      question: 'What is the cost of mobile detailing in San Bernardino, CA?',
      answer: 'Pricing varies by service package and vehicle size. We offer competitive rates for San Bernardino customers with clear pricing listed on our Services page. Packages start for sedans and scale for larger vehicles.'
    },
    {
      question: 'Do you serve Downtown San Bernardino and the Arrowhead area?',
      answer: 'Yes. We provide mobile detailing services throughout Downtown San Bernardino, the Arrowhead area, and all city neighborhoods. Our service area covers the entire San Bernardino region.'
    },
    {
      question: 'How long does a mobile detail typically take?',
      answer: 'Most detailing services take 2 to 6 hours depending on the package you choose and your vehicle condition. We work efficiently while delivering exceptional quality at your San Bernardino location.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1668279200700-b7dac51b0b83?w=1920&q=80')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">San Bernardino, California</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Mobile Car Detailing in San Bernardino, CA
            </h1>
            
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Premium mobile auto detailing delivered throughout San Bernardino. 
              From the CSUSB campus area to Shandin Hills, Downtown, and beyond, we bring professional car care directly to you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Book San Bernardino Detailing <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{businessInfo.daysOpen} • {businessInfo.hours}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Mobile Detailing in San Bernardino */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Mobile Detailing San Bernardino CA Can Rely On</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                San Bernardino residents know the challenges of keeping a vehicle clean in the Inland Empire climate. 
                Between desert dust, busy commutes, and packed schedules, finding time for professional car care can feel impossible. 
                Precision Mobile Detailing solves this problem by bringing expert auto detailing directly to your San Bernardino location.
              </p>
              <p>
                Whether you live in the foothills near Shandin Hills, attend classes at California State University San Bernardino, 
                work downtown, or reside in Verdemont or Del Rosa, our mobile service comes to you. We arrive fully equipped with 
                everything needed for a complete, showroom-quality detail, so you never have to drive to a shop or wait around.
              </p>
              <p>
                From the San Bernardino Mountains foothills to the valley floor, we serve the entire city with the same commitment 
                to quality and convenience. Our team uses professional-grade products and proven techniques to transform your vehicle 
                while you stay home, work, or handle your daily responsibilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">How Mobile Detailing Works in San Bernardino</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Book Your Appointment</h3>
                <p className="text-gray-600">Schedule online or call us. Select your detailing package and preferred time. We offer flexible scheduling throughout San Bernardino.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">We Arrive Ready</h3>
                <p className="text-gray-600">Our team shows up at your San Bernardino address with all equipment, water, and premium detailing products. No prep work needed from you.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-black text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Enjoy Your Refreshed Car</h3>
                <p className="text-gray-600">Relax while we work. When done, your vehicle looks incredible, and you saved hours by avoiding a traditional shop visit.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services in San Bernardino */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">Car Detailing Services in San Bernardino</h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              From maintenance washes to complete interior and exterior transformations, we have a mobile detailing package for every vehicle and budget.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {services.map((service, idx) => (
                <Card key={idx} className="border hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                        <Link to={service.link} className="text-black font-semibold hover:underline">
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button asChild size="lg" className="cta-button text-lg px-8 py-6">
                <Link to="/services">
                  Explore All Services & Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why San Bernardino Chooses Precision Mobile Detailing</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Professional Quality</h3>
                  <p className="text-gray-600">We use only professional-grade detailing products and techniques. Your vehicle receives the care it deserves.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Trusted Locally</h3>
                  <p className="text-gray-600">5.0-star rating from customers across the Inland Empire. Reliable, punctual, and obsessed with quality.</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">Complete Convenience</h3>
                  <p className="text-gray-600">We come to you anywhere in San Bernardino. No driving, no waiting, just professional results at your location.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area within San Bernardino */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">San Bernardino Neighborhoods We Serve</h2>
            <p className="text-center text-gray-600 mb-8 text-lg">
              Mobile car detailing throughout San Bernardino and the surrounding area.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['CSUSB Area', 'Shandin Hills', 'Verdemont', 'Downtown San Bernardino', 'Del Rosa', 'Arrowhead Area', 'University District'].map((area, idx) => (
                <span key={idx} className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700">
                  {area}
                </span>
              ))}
            </div>

            <p className="text-center text-gray-600 mt-8">
              We also provide mobile detailing in nearby <Link to="/service-areas/redlands-ca" className="text-black font-semibold hover:underline">Redlands</Link>, <Link to="/service-areas/riverside-ca" className="text-black font-semibold hover:underline">Riverside</Link>, and <Link to="/service-areas/rancho-cucamonga-ca" className="text-black font-semibold hover:underline">Rancho Cucamonga</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Mobile Detailing in San Bernardino Today</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Professional car detailing at your doorstep. Schedule your San Bernardino appointment now.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Get a Quote <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/service-areas">
                View All Service Areas
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SanBernardinoCA;
